// src/game/SoundManager.ts
export class SoundManager {
    private static sounds: Record<string, HTMLAudioElement> = {};
    private static pathPools: Record<string, HTMLAudioElement[]> = {};
    private static enabled: boolean = true;
    private static currentMusic: string | null = null;
    
    // Preload sounds
    static preloadSounds() {
      this.loadSound('jump', '/sounds/jump.mp3');
      this.loadSound('pepePunch', '/sounds/pepepunch.mp3');
      this.loadSound('dogePunch', '/sounds/dogepunch.mp3');
      this.loadSound('trollfacePunch', '/sounds/trollfacepunch.mp3');
      this.loadSound('wifejakPunch', '/sounds/wifejakpunch.mp3');
      this.loadSound('gigachadPunch', '/sounds/gigachadpunch.mp3');
      this.loadSound('fight', '/sounds/fight.mp3');
      this.loadSound('music', '/MemeGame.mp3');
      this.primePath('/block.mp3');
      this.primePath('/victory.mp3');
      this.primePath('/sounds/jump.mp3');
      this.primePath('/sounds/portal.mp3');
      this.primePath('/portalhit.mp3');
    }
    
    static loadSound(id: string, path: string) {
      // Check if already loaded
      if (this.sounds[id]) {
        //console.log(`Sound ${id} already loaded`);
        return this.sounds[id];
      }
      
      const audio = new Audio(path);
      this.sounds[id] = audio;
      
      // Preload by setting src
      audio.load();
      return audio;
    }
    
    static playSound(id: string, volume: number = 0.5, loop: boolean = false) {
      if (!this.enabled) return;
      
      try {
        const sound = this.sounds[id];
        if (sound) {
          // For music tracks, check if already playing
          if (id === 'music') {
            // If this music is already playing, don't restart it
            if (this.currentMusic === id && !sound.paused) {
              console.log("Music already playing, not restarting");
              return;
            }
            // Remember the current music track
            this.currentMusic = id;
          }
          
          sound.currentTime = 0; // Reset to beginning
          sound.volume = volume;
          sound.loop = loop;
          sound.play().catch(err => console.error(`Error playing sound ${id}:`, err));
        } else {
          console.warn(`Sound not found: ${id}`);
        }
      } catch (err) {
        console.error(`Error playing sound ${id}:`, err);
      }
    }

    static primePath(path: string, poolSize: number = 3) {
      if (this.pathPools[path]) {
        return this.pathPools[path];
      }

      const pool = Array.from({ length: poolSize }, () => {
        const audio = new Audio(path);
        audio.preload = 'auto';
        audio.load();
        return audio;
      });

      this.pathPools[path] = pool;
      return pool;
    }

    static playPath(path: string, volume: number = 0.5) {
      if (!this.enabled) return;

      try {
        const pool = this.primePath(path);
        const sound = pool.find(audio => audio.paused || audio.ended) || pool[0];

        sound.pause();
        sound.currentTime = 0;
        sound.volume = volume;
        sound.loop = false;
        sound.play().catch(err => console.error(`Error playing sound ${path}:`, err));
      } catch (err) {
        console.error(`Error playing sound ${path}:`, err);
      }
    }
    
    static stopSound(id: string) {
      try {
        const sound = this.sounds[id];
        if (sound) {
          console.log(`Stopping sound ${id}`);
          
          // For music, reset the current track marker
          if (id === 'music') {
            this.currentMusic = null;
          }
          
          sound.pause();
          sound.currentTime = 0;
        }
      } catch (err) {
        console.error(`Error stopping sound ${id}:`, err);
      }
    }
    
    static stopAllSounds() {
      Object.keys(this.sounds).forEach(id => {
        this.stopSound(id);
      });
      Object.values(this.pathPools).forEach(pool => {
        pool.forEach(sound => {
          sound.pause();
          sound.currentTime = 0;
        });
      });
      this.currentMusic = null;
    }
    
    static setEnabled(enabled: boolean) {
      this.enabled = enabled;
      if (!enabled) {
        // Stop all sounds if disabled
        this.stopAllSounds();
      }
    }
    
    static isEnabled() {
      return this.enabled;
    }
    
    static isMusicPlaying() {
      return this.currentMusic !== null && 
             this.sounds[this.currentMusic] && 
             !this.sounds[this.currentMusic].paused;
    }
}
